<template>
  <div
    class="power-up-focused-page d-flex flex-column"
    :class="{ 'power-up-focused-page--embedded': embedded }"
  >
    <v-toolbar
      density="comfortable"
      flat
      color="surface"
      class="power-up-focused-page__toolbar border-b"
    >
      <v-btn
        v-if="showBack && navigationKind === 'href'"
        icon="mdi-arrow-left"
        variant="text"
        class="power-up-focused-page__toolbar-back"
        :href="backHref"
        :rel="backRel || undefined"
        :aria-label="backAriaLabel"
      />
      <v-btn
        v-else-if="showBack && navigationKind === 'to'"
        icon="mdi-arrow-left"
        variant="text"
        class="power-up-focused-page__toolbar-back"
        :to="backTo"
        :aria-label="backAriaLabel"
      />
      <v-btn
        v-else-if="showBack"
        icon="mdi-arrow-left"
        variant="text"
        class="power-up-focused-page__toolbar-back"
        :aria-label="backAriaLabel"
        @click="emit('back')"
      />
      <div
        v-else
        class="power-up-focused-page__toolbar-end"
        aria-hidden="true"
      />

      <v-toolbar-title
        class="text-h6 font-weight-medium text-center power-up-focused-page__toolbar-title"
      >
        {{ title }}
      </v-toolbar-title>

      <slot name="toolbar-trailing">
        <div class="power-up-focused-page__toolbar-end" aria-hidden="true" />
      </slot>
    </v-toolbar>

    <div
      class="power-up-focused-page__body flex-grow-1 pa-4"
      :class="{
        'power-up-focused-page__body--with-footer': showFooter && !embedded,
      }"
    >
      <slot />
    </div>

    <div
      v-if="showFooter"
      class="power-up-focused-page__footer pa-4"
      :class="{ 'power-up-focused-page__footer--embedded': embedded }"
    >
      <slot name="footer" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";

const props = withDefaults(
  defineProps<{
    title: string;
    /** When false, the leading control is a spacer (toolbar title stays centered). */
    showBack?: boolean;
    /** Used when `showBack` is true (ignored when `showBack` is false). */
    backAriaLabel?: string;
    backTo?: string | Record<string, unknown>;
    backHref?: string;
    backRel?: string;
    useBackClick?: boolean;
    showFooter?: boolean;
    /**
     * Default behavior: the layout flows with the document (no full-viewport
     * min-height, no internally-scrolling body, no fixed CTA footer) so the
     * surrounding page chrome — most importantly the marketing site footer in
     * PowerUpLayout — renders directly below the page.
     *
     * Set to `false` to restore the legacy "focused" behavior where the page
     * fills the viewport and the CTA footer is fixed to the bottom.
     */
    embedded?: boolean;
  }>(),
  {
    showBack: true,
    backAriaLabel: "",
    useBackClick: false,
    showFooter: false,
    embedded: true,
  },
);

const emit = defineEmits<{
  back: [];
}>();

const navigationKind = computed<"href" | "to" | "click">(() => {
  if (!props.showBack) return "click";
  if (props.useBackClick) return "click";
  if (props.backHref) return "href";
  if (props.backTo != null) {
    if (typeof props.backTo === "string") {
      return props.backTo.length > 0 ? "to" : "click";
    }
    return "to";
  }
  return "click";
});
</script>

<style scoped>
/* Match parts marketplace detail: toolbar + fixed CTA (Detail.vue). */
.power-up-focused-page {
  min-height: calc(100dvh - 80px);
  background: rgb(var(--v-theme-surface));
}

/*
 * Embedded mode lets the layout flow with the document so any page chrome
 * underneath (e.g. the site-wide marketing footer in PowerUpLayout) becomes
 * visible directly below the page.
 */
.power-up-focused-page--embedded {
  min-height: 0;
}

.border-b {
  border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.power-up-focused-page__toolbar-back,
.power-up-focused-page__toolbar-end {
  flex: 0 0 48px;
  width: 48px;
}

.power-up-focused-page__toolbar-title {
  flex: 1 1 auto;
  max-width: none;
}

.power-up-focused-page__body {
  min-height: 0;
  overflow: auto;
  padding-bottom: calc(16px + env(safe-area-inset-bottom, 0px));
}

.power-up-focused-page--embedded .power-up-focused-page__body {
  overflow: visible;
}

.power-up-focused-page__body--with-footer {
  padding-bottom: calc(88px + env(safe-area-inset-bottom, 0px));
}

.power-up-focused-page__footer {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 5;
  padding-bottom: calc(16px + env(safe-area-inset-bottom, 0px));
  background: rgb(var(--v-theme-surface));
  border-top: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.06);
}

/* In embedded mode the CTA flows with content so the marketing footer below
 * is reachable rather than being permanently overlaid. */
.power-up-focused-page__footer--embedded {
  position: static;
  left: auto;
  right: auto;
  bottom: auto;
  z-index: auto;
  padding-bottom: 16px;
  box-shadow: none;
}
</style>
